import React, { Fragment, useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from "@headlessui/react";
import {
  EllipsisHorizontalIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { Button } from "components/ui";
import clsx from "clsx";

// Registry for row action handlers
const weakRowActionRegistry = new WeakMap();

export function registerRowActions(item, actionProps) {
  if (item && typeof item === "object") {
    weakRowActionRegistry.set(item, actionProps);
  }
}

export function getRowActions(item) {
  if (item && typeof item === "object") {
    return weakRowActionRegistry.get(item);
  }
  return null;
}

export function getAdjustedPosition(x, y) {
  let adjustedX = x;
  let adjustedY = y;
  // Account for menu dimensions (~160px width, ~150px height)
  if (x + 160 > window.innerWidth - 10) {
    adjustedX = Math.max(10, window.innerWidth - 170);
  }
  if (y + 150 > window.innerHeight - 10) {
    adjustedY = Math.max(10, window.innerHeight - 160);
  }
  return { top: adjustedY, left: adjustedX };
}

export function triggerRowContextMenu(e, item) {
  window.dispatchEvent(new CustomEvent("close-table-context-menu"));
  const actions = getRowActions(item);
  if (actions && window.innerWidth >= 768) {
    e.preventDefault();
    const pos = getAdjustedPosition(e.clientX, e.clientY);
    window.dispatchEvent(
      new CustomEvent("open-table-context-menu", {
        detail: {
          x: pos.left,
          y: pos.top,
          item,
          actions,
        },
      })
    );
    return true;
  }
  return false;
}

// Global floating Context Menu component
export function GlobalTableContextMenu() {
  const [menuState, setMenuState] = useState({
    show: false,
    x: 0,
    y: 0,
    item: null,
    actions: null,
  });

  const menuRef = useRef(null);

  const handleClose = useCallback(() => {
    setMenuState((prev) => ({ ...prev, show: false }));
  }, []);

  useEffect(() => {
    const handleOpen = (e) => {
      const { x, y, item, actions } = e.detail || {};
      setMenuState({
        show: true,
        x,
        y,
        item,
        actions,
      });
    };

    const handleCloseEvent = () => {
      handleClose();
    };

    window.addEventListener("open-table-context-menu", handleOpen);
    window.addEventListener("close-table-context-menu", handleCloseEvent);
    return () => {
      window.removeEventListener("open-table-context-menu", handleOpen);
      window.removeEventListener("close-table-context-menu", handleCloseEvent);
    };
  }, [handleClose]);

  const { show, x, y, item, actions } = menuState;

  useEffect(() => {
    if (!show) return;

    const handleOutsideClick = (e) => {
      if (e.button === 2) return;
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        handleClose();
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        handleClose();
      }
    };

    const handleScroll = () => {
      handleClose();
    };

    window.addEventListener("mousedown", handleOutsideClick);
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("scroll", handleScroll, true);

    return () => {
      window.removeEventListener("mousedown", handleOutsideClick);
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [show, handleClose]);

  if (!show || !actions) return null;

  const {
    onView,
    onEdit,
    onDelete,
    canView = true,
    canEdit = true,
    canDelete = true,
    editDisabled = false,
    editDisabledTooltip,
    deleteDisabled = false,
    deleteDisabledTooltip,
    viewLabel = "View",
    editLabel = "Edit",
    deleteLabel = "Delete",
    customActions = [],
  } = actions;

  const hasView = canView && Boolean(onView);
  const hasEdit = canEdit && Boolean(onEdit);
  const hasDelete = canDelete && Boolean(onDelete);
  const hasCustom = customActions && customActions.length > 0;

  if (!hasView && !hasEdit && !hasDelete && !hasCustom) {
    return null;
  }

  const handleView = (e) => {
    e.stopPropagation();
    handleClose();
    if (onView) onView(item);
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    if (editDisabled) return;
    handleClose();
    if (onEdit) onEdit(item);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    if (deleteDisabled) return;
    handleClose();
    if (onDelete) onDelete(item);
  };

  return createPortal(
    <div
      ref={menuRef}
      style={{ top: `${y}px`, left: `${x}px` }}
      className="fixed z-[9999] min-w-[11.5rem] w-max rounded-lg border border-gray-300 bg-white py-1 shadow-lg shadow-gray-200/50 outline-hidden dark:border-dark-500 dark:bg-dark-750 dark:shadow-none animate-in fade-in zoom-in-95 duration-100"
      onClick={(e) => e.stopPropagation()}
    >
      {hasView && (
        <button
          type="button"
          onClick={handleView}
          className="flex h-9 w-full items-center gap-3 px-3 tracking-wide outline-hidden transition-colors text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-dark-200 dark:hover:bg-dark-600 dark:hover:text-white cursor-pointer whitespace-nowrap"
        >
          <EyeIcon className="size-4 stroke-[1.8] shrink-0 text-teal-500" />
          <span className="whitespace-nowrap">{viewLabel}</span>
        </button>
      )}

      {hasEdit && (
        <button
          type="button"
          onClick={handleEdit}
          disabled={editDisabled}
          title={editDisabled ? editDisabledTooltip : undefined}
          aria-disabled={editDisabled}
          className={clsx(
            "flex h-9 w-full items-center gap-3 px-3 tracking-wide outline-hidden transition-colors text-sm whitespace-nowrap",
            editDisabled
              ? "text-gray-350 dark:text-dark-500 cursor-not-allowed"
              : "text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-dark-200 dark:hover:bg-dark-600 dark:hover:text-white cursor-pointer"
          )}
        >
          <PencilIcon className={clsx("size-4 stroke-[1.8] shrink-0", editDisabled ? "text-gray-350 dark:text-dark-500" : "text-amber-500")} />
          <span className="whitespace-nowrap">{editLabel}</span>
        </button>
      )}

      {hasCustom &&
        customActions.map((action, idx) => {
          if (action.hidden) return null;
          const IconComponent = action.icon;
          return (
            <button
              key={action.key || idx}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleClose();
                if (action.onClick) action.onClick(item);
              }}
              className={clsx(
                "flex h-9 w-full items-center gap-3 px-3 text-sm outline-hidden transition-colors cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-600 whitespace-nowrap",
                action.danger
                  ? "text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                  : "text-gray-700 dark:text-dark-200"
              )}
            >
              {IconComponent && (
                <IconComponent
                  className={clsx(
                    "size-4 stroke-[1.8] shrink-0",
                    action.iconColor || action.iconClass
                  )}
                />
              )}
              <span className="whitespace-nowrap">{action.label}</span>
            </button>
          );
        })}

      {hasDelete && (
        <button
          type="button"
          onClick={handleDelete}
          disabled={deleteDisabled}
          title={deleteDisabled ? deleteDisabledTooltip : undefined}
          aria-disabled={deleteDisabled}
          className={clsx(
            "flex h-9 w-full items-center gap-3 px-3 tracking-wide outline-hidden transition-colors text-sm whitespace-nowrap",
            deleteDisabled
              ? "text-gray-350 dark:text-dark-500 cursor-not-allowed"
              : "text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 hover:bg-gray-100 dark:hover:bg-dark-600 cursor-pointer"
          )}
        >
          <TrashIcon className={clsx("size-4 stroke-[1.8] shrink-0", deleteDisabled ? "text-gray-350 dark:text-dark-500" : "text-red-500")} />
          <span className="whitespace-nowrap">{deleteLabel}</span>
        </button>
      )}
    </div>,
    document.body
  );
}

// Standalone TableContextMenu for custom/manual usages
export function TableContextMenu({
  show,
  x,
  y,
  onClose,
  item,
  onView,
  onEdit,
  onDelete,
  canView = true,
  canEdit = true,
  canDelete = true,
  editDisabled = false,
  editDisabledTooltip,
  deleteDisabled = false,
  deleteDisabledTooltip,
  viewLabel = "View",
  editLabel = "Edit",
  deleteLabel = "Delete",
  customActions = [],
}) {
  const menuRef = useRef(null);
  const pos = getAdjustedPosition(x, y);

  useEffect(() => {
    if (!show) return;

    const handleOutsideClick = (e) => {
      if (e.button === 2) return;
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        onClose?.();
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose?.();
      }
    };

    const handleScroll = () => {
      onClose?.();
    };

    const handleCloseEvent = () => {
      onClose?.();
    };

    window.addEventListener("mousedown", handleOutsideClick);
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("close-table-context-menu", handleCloseEvent);

    return () => {
      window.removeEventListener("mousedown", handleOutsideClick);
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("close-table-context-menu", handleCloseEvent);
    };
  }, [show, onClose]);

  if (!show) return null;

  const hasView = canView && Boolean(onView);
  const hasEdit = canEdit && Boolean(onEdit);
  const hasDelete = canDelete && Boolean(onDelete);
  const hasCustom = customActions && customActions.length > 0;

  if (!hasView && !hasEdit && !hasDelete && !hasCustom) {
    return null;
  }

  const handleView = (e) => {
    e.stopPropagation();
    onClose?.();
    if (onView) onView(item);
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    if (editDisabled) return;
    onClose?.();
    if (onEdit) onEdit(item);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    if (deleteDisabled) return;
    onClose?.();
    if (onDelete) onDelete(item);
  };

  return createPortal(
    <div
      ref={menuRef}
      style={{ top: `${pos.top}px`, left: `${pos.left}px` }}
      className="fixed z-[9999] min-w-[11.5rem] w-max rounded-lg border border-gray-300 bg-white py-1 shadow-lg shadow-gray-200/50 outline-hidden dark:border-dark-500 dark:bg-dark-750 dark:shadow-none animate-in fade-in zoom-in-95 duration-100"
      onClick={(e) => e.stopPropagation()}
    >
      {hasView && (
        <button
          type="button"
          onClick={handleView}
          className="flex h-9 w-full items-center gap-3 px-3 tracking-wide outline-hidden transition-colors text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-dark-200 dark:hover:bg-dark-600 dark:hover:text-white cursor-pointer whitespace-nowrap"
        >
          <EyeIcon className="size-4 stroke-[1.8] shrink-0 text-teal-500" />
          <span className="whitespace-nowrap">{viewLabel}</span>
        </button>
      )}

      {hasEdit && (
        <button
          type="button"
          onClick={handleEdit}
          disabled={editDisabled}
          title={editDisabled ? editDisabledTooltip : undefined}
          aria-disabled={editDisabled}
          className={clsx(
            "flex h-9 w-full items-center gap-3 px-3 tracking-wide outline-hidden transition-colors text-sm whitespace-nowrap",
            editDisabled
              ? "text-gray-350 dark:text-dark-500 cursor-not-allowed"
              : "text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-dark-200 dark:hover:bg-dark-600 dark:hover:text-white cursor-pointer"
          )}
        >
          <PencilIcon className={clsx("size-4 stroke-[1.8] shrink-0", editDisabled ? "text-gray-350 dark:text-dark-500" : "text-amber-500")} />
          <span className="whitespace-nowrap">{editLabel}</span>
        </button>
      )}

      {hasCustom &&
        customActions.map((action, idx) => {
          if (action.hidden) return null;
          const IconComponent = action.icon;
          return (
            <button
              key={action.key || idx}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onClose?.();
                if (action.onClick) action.onClick(item);
              }}
              className={clsx(
                "flex h-9 w-full items-center gap-3 px-3 text-sm outline-hidden transition-colors cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-600 whitespace-nowrap",
                action.danger
                  ? "text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                  : "text-gray-700 dark:text-dark-200"
              )}
            >
              {IconComponent && (
                <IconComponent
                  className={clsx(
                    "size-4 stroke-[1.8] shrink-0",
                    action.iconColor || action.iconClass
                  )}
                />
              )}
              <span className="whitespace-nowrap">{action.label}</span>
            </button>
          );
        })}

      {hasDelete && (
        <button
          type="button"
          onClick={handleDelete}
          disabled={deleteDisabled}
          title={deleteDisabled ? deleteDisabledTooltip : undefined}
          aria-disabled={deleteDisabled}
          className={clsx(
            "flex h-9 w-full items-center gap-3 px-3 tracking-wide outline-hidden transition-colors text-sm whitespace-nowrap",
            deleteDisabled
              ? "text-gray-350 dark:text-dark-500 cursor-not-allowed"
              : "text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 hover:bg-gray-100 dark:hover:bg-dark-600 cursor-pointer"
          )}
        >
          <TrashIcon className={clsx("size-4 stroke-[1.8] shrink-0", deleteDisabled ? "text-gray-350 dark:text-dark-500" : "text-red-500")} />
          <span className="whitespace-nowrap">{deleteLabel}</span>
        </button>
      )}
    </div>,
    document.body
  );
}

// Custom hook for explicit context menu management
export function useTableContextMenu() {
  const [contextMenu, setContextMenu] = useState({
    show: false,
    x: 0,
    y: 0,
    item: null,
  });

  const onRowContextMenu = useCallback((e, rowData) => {
    if (window.innerWidth >= 768) {
      e.preventDefault();
      setContextMenu({
        show: true,
        x: e.clientX,
        y: e.clientY,
        item: rowData,
      });
    }
  }, []);

  const handleCloseContextMenu = useCallback(() => {
    setContextMenu({ show: false, x: 0, y: 0, item: null });
  }, []);

  const renderContextMenu = useCallback(
    (props) => (
      <TableContextMenu
        show={contextMenu.show}
        x={contextMenu.x}
        y={contextMenu.y}
        onClose={handleCloseContextMenu}
        item={contextMenu.item}
        {...props}
      />
    ),
    [contextMenu, handleCloseContextMenu]
  );

  return {
    contextMenu,
    onRowContextMenu,
    handleCloseContextMenu,
    renderContextMenu,
    TableContextMenu: renderContextMenu,
  };
}

function TableActionMenuListener({ open, close }) {
  useEffect(() => {
    if (!open) return;

    const handleOpenContextMenu = () => {
      close();
    };

    window.addEventListener("open-table-context-menu", handleOpenContextMenu);

    return () => {
      window.removeEventListener("open-table-context-menu", handleOpenContextMenu);
    };
  }, [open, close]);

  return null;
}

export default function TableActionMenu({
  onView,
  onEdit,
  onDelete,
  item,
  canView = true,
  canEdit = true,
  canDelete = true,
  editDisabled = false,
  editDisabledTooltip,
  deleteDisabled = false,
  deleteDisabledTooltip,
  viewLabel = "View",
  editLabel = "Edit",
  deleteLabel = "Delete",
  customActions = [],
  className,
  buttonClassName,
  align = "end",
}) {
  useEffect(() => {
    if (item) {
      registerRowActions(item, {
        onView,
        onEdit,
        onDelete,
        canView,
        canEdit,
        canDelete,
        editDisabled,
        editDisabledTooltip,
        deleteDisabled,
        deleteDisabledTooltip,
        viewLabel,
        editLabel,
        deleteLabel,
        customActions,
      });
    }
  }, [
    item,
    onView,
    onEdit,
    onDelete,
    canView,
    canEdit,
    canDelete,
    editDisabled,
    editDisabledTooltip,
    deleteDisabled,
    deleteDisabledTooltip,
    viewLabel,
    editLabel,
    deleteLabel,
    customActions,
  ]);

  const handleView = (e) => {
    e.stopPropagation();
    if (onView) onView(item);
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    if (editDisabled) return;
    if (onEdit) onEdit(item);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    if (deleteDisabled) return;
    if (onDelete) onDelete(item);
  };

  const hasView = canView && Boolean(onView);
  const hasEdit = canEdit && Boolean(onEdit);
  const hasDelete = canDelete && Boolean(onDelete);
  const hasCustom = customActions && customActions.length > 0;

  if (!hasView && !hasEdit && !hasDelete && !hasCustom) {
    return null;
  }

  return (
    <div
      className={clsx("flex items-center justify-end", className)}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <Menu as="div" className="relative inline-block text-left">
        {({ open, close }) => (
          <>
            <TableActionMenuListener open={open} close={close} />
            <MenuButton
              as={Button}
              variant="flat"
              isIcon
              className={clsx("size-8 rounded-full", buttonClassName)}
              onClick={(e) => {
                e.stopPropagation();
                window.dispatchEvent(new CustomEvent("close-table-context-menu"));
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
                window.dispatchEvent(new CustomEvent("close-table-context-menu"));
              }}
            >
              <EllipsisHorizontalIcon className="size-4.5" />
            </MenuButton>
            <Transition
              as={Fragment}
              enter="transition ease-out"
              enterFrom="opacity-0 translate-y-2"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-2"
            >
              <MenuItems
                anchor={`bottom ${align}`}
                portal
                className="dark:bg-dark-750 dark:border-dark-500 z-50 min-w-[11.5rem] w-max rounded-lg border border-gray-300 bg-white py-1 shadow-lg shadow-gray-200/50 outline-hidden focus-visible:outline-hidden dark:shadow-none"
              >
                {hasView && (
                  <MenuItem>
                    {({ focus }) => (
                      <button
                        type="button"
                        onClick={handleView}
                        className={clsx(
                          "flex h-9 w-full items-center gap-3 px-3 tracking-wide outline-hidden transition-colors text-sm text-gray-700 dark:text-dark-200 cursor-pointer whitespace-nowrap",
                          focus && "bg-gray-100 dark:bg-dark-600 text-gray-900 dark:text-white"
                        )}
                      >
                        <EyeIcon className="size-4 stroke-[1.8] shrink-0 text-teal-500" />
                        <span className="whitespace-nowrap">{viewLabel}</span>
                      </button>
                    )}
                  </MenuItem>
                )}

                {hasEdit && (
                  <MenuItem disabled={editDisabled}>
                    {({ focus }) => (
                      <button
                        type="button"
                        onClick={handleEdit}
                        disabled={editDisabled}
                        title={editDisabled ? editDisabledTooltip : undefined}
                        aria-disabled={editDisabled}
                        className={clsx(
                          "flex h-9 w-full items-center gap-3 px-3 tracking-wide outline-hidden transition-colors text-sm whitespace-nowrap",
                          editDisabled
                            ? "text-gray-350 dark:text-dark-500 cursor-not-allowed"
                            : clsx(
                                "text-gray-700 dark:text-dark-200 cursor-pointer",
                                focus && "bg-gray-100 dark:bg-dark-600 text-gray-900 dark:text-white"
                              )
                        )}
                      >
                        <PencilIcon className={clsx("size-4 stroke-[1.8] shrink-0", editDisabled ? "text-gray-350 dark:text-dark-500" : "text-amber-500")} />
                        <span className="whitespace-nowrap">{editLabel}</span>
                      </button>
                    )}
                  </MenuItem>
                )}

                {hasCustom &&
                  customActions.map((action, idx) => {
                    if (action.hidden) return null;
                    const IconComponent = action.icon;
                    return (
                      <MenuItem key={action.key || idx}>
                        {({ focus }) => (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (action.onClick) action.onClick(item);
                            }}
                            className={clsx(
                              "flex h-9 w-full items-center gap-3 px-3 outline-hidden transition-colors text-sm cursor-pointer whitespace-nowrap",
                              action.danger
                                ? "text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                                : "text-gray-700 dark:text-dark-200",
                              focus && "bg-gray-100 dark:bg-dark-600"
                            )}
                          >
                            {IconComponent && (
                              <IconComponent
                                className={clsx(
                                  "size-4 stroke-[1.8] shrink-0",
                                  action.iconColor || action.iconClass
                                )}
                              />
                            )}
                            <span className="whitespace-nowrap">{action.label}</span>
                          </button>
                        )}
                      </MenuItem>
                    );
                  })}

                {hasDelete && (
                  <MenuItem disabled={deleteDisabled}>
                    {({ focus }) => (
                      <button
                        type="button"
                        onClick={handleDelete}
                        disabled={deleteDisabled}
                        title={deleteDisabled ? deleteDisabledTooltip : undefined}
                        aria-disabled={deleteDisabled}
                        className={clsx(
                          "flex h-9 w-full items-center gap-3 px-3 tracking-wide outline-hidden transition-colors text-sm whitespace-nowrap",
                          deleteDisabled
                            ? "text-gray-350 dark:text-dark-500 cursor-not-allowed"
                            : clsx(
                                "text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 cursor-pointer",
                                focus && "bg-gray-100 dark:bg-dark-600"
                              )
                        )}
                      >
                        <TrashIcon className={clsx("size-4 stroke-[1.8] shrink-0", deleteDisabled ? "text-gray-350 dark:text-dark-500" : "text-red-500")} />
                        <span className="whitespace-nowrap">{deleteLabel}</span>
                      </button>
                    )}
                  </MenuItem>
                )}
              </MenuItems>
            </Transition>
          </>
        )}
      </Menu>
    </div>
  );
}
